import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { usePermission } from '../auth/AuthContext';
import { BUTTON_SHAPE, BUTTON_SURFACES } from './buttonSurface';
import { Menu } from './Menu';
import type { MenuItem } from './Menu';
import { Tooltip } from './Tooltip';

/**
 * ONE action with a menu of related ones attached to its trailing edge — a split button.
 *
 * <b>It is not `createMenu`, and the difference is the primary action.</b> `<IndexToolbar>`'s
 * `createMenu` REPLACES the Create button with a dropdown, for a module with more than one
 * thing to create: pressing it asks which. This keeps the one primary action pressable in one
 * click and hangs everything ELSE the surface offers off the chevron beside it — so the
 * commonest act costs nothing and the rest stop taking a row of the page.
 *
 * <b>The WRAPPER owns the shape, and the segments own nothing of it.</b> That is the
 * composition `<SearchGroup>` and `<FieldGroup>` already draw: one radius, one outline, and a
 * `border-l` where the two meet — which is what makes a reader see one control rather than two
 * buttons that happen to touch. It is also the only version that is robust here: two `rounded`
 * utilities are the same CSS property at the same specificity, so a per-segment override would
 * be decided by Tailwind's stylesheet order rather than by this file.
 *
 * <b>So the ring is INSET.</b> The wrapper clips, and `<Button>`'s outset ring would be sliced
 * off at exactly the edge it is drawn on. A ring on the wrapper instead — which is what
 * `<FieldGroup>` does — cannot say WHICH segment has focus, and here the two segments do
 * different things: a keyboard reader has to know whether Enter performs the action or opens a
 * menu.
 *
 * <b>The two halves are gated and disabled INDEPENDENTLY, and that is the whole reason this is
 * a component rather than two buttons in a div.</b> The menu's entries belong to other modules
 * and carry their own keys, so gating the group on the ACTION's permission would hide them
 * from a reader entitled to every one — the over-grant this product names as "a control
 * carries the key of what it does, not the key of the page it sits on", arriving backwards.
 * Four shapes fall out of that, and each is what is honestly true:
 *
 * ```
 * may act, has entries    the split button
 * may act, no entries     the action alone — no chevron onto an empty panel
 * may NOT act, entries    the MENU alone, carrying `menuLabel` as its visible label
 * may NOT act, no entries nothing
 * ```
 *
 * The third is the one worth stating: the trailing segment grows a label rather than standing
 * as a lone chevron, because a chevron with nothing beside it names nothing at all.
 */
export function SplitButton({
  id,
  label,
  leading,
  onClick,
  actionPermission,
  disabledReason = null,
  menuId,
  menuLabel,
  items,
  menuDisabledReason = null,
  align = 'left',
}: {
  id: string;
  /** The primary action's own label — the words on the button, never a generic "Create". */
  label: ReactNode;
  leading?: ReactNode;
  onClick: () => void;
  /** The key of what the ACTION does. It hides that segment alone, never the menu. */
  actionPermission?: string;
  /** Non-null disables the ACTION segment and says why. The menu is unaffected. */
  disabledReason?: string | null;
  menuId: string;
  /**
   * The trailing segment's accessible name AND its tooltip — it is icon-only, so it owes both.
   * "More actions" is the wrong kind of word here; name what is behind it.
   */
  menuLabel: string;
  items: MenuItem[];
  /** Non-null disables the MENU segment and says why. The action is unaffected. */
  menuDisabledReason?: string | null;
  align?: 'left' | 'right';
}) {
  const mayAct = usePermission(actionPermission ?? '') || !actionPermission;

  const actionDisabled = Boolean(disabledReason?.trim());
  const menuDisabled = Boolean(menuDisabledReason?.trim());
  const hasMenu = items.length > 0;

  if (!mayAct && !hasMenu) return null;

  const segment = (extra: string) =>
    [
      BUTTON_SHAPE,
      /*
        INSET, because the wrapper clips — see the note above. `brand-200` rather than the
        `brand-300` `<Button>` rings with: this one is drawn ON the brand ground rather than
        outside it, so it has to be lighter than what it sits on to read at all.
      */
      'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-200',
      BUTTON_SURFACES.primary,
      extra,
    ].join(' ');

  const trigger = (chevronOnly: boolean) =>
    ({ toggle }: { toggle: () => void }) => (
      <Tooltip label={menuLabel} disabledReason={menuDisabledReason}>
        <button
          id={`${menuId}-trigger`}
          type="button"
          onClick={toggle}
          disabled={menuDisabled}
          aria-label={chevronOnly ? menuLabel : undefined}
          className={segment(
            chevronOnly
              ? /*
                  NARROWER than the action beside it — `px-2` against `px-4`. A chevron is not a
                  label, and padded to the same width the trailing segment reads as a second
                  button of equal weight rather than as this one's own menu.

                  The divider is WHITE — the label's own colour, and the one tone on this button
                  that does not move with the ground. Product call, 28 Sep 2026. It was
                  `brand-700`, a darker step of the same hue, which reads at rest and then all
                  but disappears under the hover that takes the segment to brand-600: a divider
                  that fades under exactly the pointer about to press it is one drawn for the
                  screenshot rather than for the reader.

                  IT IS `border-l-white`, THE SIDE-SPECIFIC COLOUR, not `border-white`
                  (16 Oct 2026). `BUTTON_SURFACES.primary` now carries `border border-brand-500`
                  on all four sides, and a plain `border-white` also sets all four — Tailwind
                  would resolve the two by STYLESHEET order rather than by the order written
                  here, which is the hazard `BUTTON_SIZES` is a map to avoid. Naming the side
                  leaves the other three on the brand.

                  `-ml-px` overlaps the two segments' touching borders into ONE pixel instead of
                  two. Only ever applied BESIDE the action — `trigger(false)` passes no extra, so
                  the menu-alone branch is not pulled sideways.
                */
                '-ml-px border-l-white px-2'
              : '',
          )}
        >
          {chevronOnly ? null : <span>{menuLabel}</span>}
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </Tooltip>
    );

  /*
    THE MENU ALONE. The reader may not perform the action, and the entries behind the chevron
    are not the action's to gate — so the trailing segment becomes an ordinary labelled button
    rather than a chevron floating where a button used to be.
  */
  if (!mayAct) {
    return (
      <span className="inline-flex overflow-hidden rounded-lg">
        <Menu id={menuId} items={items} align={align} trigger={trigger(false)} />
      </span>
    );
  }

  const action = (
    <Tooltip disabledReason={disabledReason}>
      <button
        id={id}
        type="button"
        onClick={onClick}
        disabled={actionDisabled}
        className={segment('')}
      >
        {leading}
        <span>{label}</span>
      </button>
    </Tooltip>
  );

  return (
    <span className="inline-flex overflow-hidden rounded-lg">
      {action}
      {hasMenu ? <Menu id={menuId} items={items} align={align} trigger={trigger(true)} /> : null}
    </span>
  );
}
